import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../components/auth/AuthProvider';
import { downloadCertificate, fetchEventCheckoutClientSecret, finalizeEventRegistrationPayment, getAvailableEventById, listAvailableEvents, listMyRegistrations, registerToEvent } from '../../controllers/events';
import { stripeKey } from '../../controllers/config';

function formatSessionTime(value) {
  if (!value) {
    return '-';
  }
  return String(value).slice(0, 5);
}

function SessionDetailsModal({ show, onHide, title, sessions }) {
  const rows = Array.isArray(sessions) ? sessions : [];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Sesiones de {title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {rows.length ? (
          <div className="d-flex flex-column gap-3">
            {rows.map((session) => (
              <Card key={session.event_session_id || `${session.session_date}-${session.start_time}`}>
                <Card.Body>
                  <Row className="g-2 small">
                    <Col md={6}><strong>Fecha:</strong> {session.session_date || '-'}</Col>
                    <Col md={3}><strong>Hora inicio:</strong> {formatSessionTime(session.start_time)}</Col>
                    <Col md={3}><strong>Hora fin:</strong> {formatSessionTime(session.end_time)}</Col>
                    <Col md={6}><strong>Lugar:</strong> {session.location_name || 'Sin lugar definido'}</Col>
                    <Col md={6}><strong>Direccion:</strong> {session.location_address || 'Sin direccion definida'}</Col>
                    <Col md={12}>
                      <strong>Enlace:</strong>{' '}
                      {session.meeting_url ? (
                        <a href={session.meeting_url} target="_blank" rel="noreferrer">{session.meeting_url}</a>
                      ) : (
                        'Sin enlace'
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
          </div>
        ) : (
          <Alert variant="info" className="mb-0">No hay sesiones registradas para este evento.</Alert>
        )}
      </Modal.Body>
    </Modal>
  );
}

function EventLookupModal({ show, onHide, eventItem, registration, peopleCount, onPeopleChange, onRegister, busy }) {
  if (!eventItem) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Evento #{eventItem.event_id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-3">
          <div>
            <h4 className="mb-1">{eventItem.title}</h4>
            <div className="text-muted">{eventItem.event_type}</div>
          </div>
          <Row className="g-2 small">
            <Col md={6}><strong>Inicio:</strong> {eventItem.starts_on || '-'}</Col>
            <Col md={6}><strong>Fin:</strong> {eventItem.ends_on || '-'}</Col>
            <Col md={6}><strong>Modalidad:</strong> {eventItem.modality || '-'}</Col>
            <Col md={6}><strong>Pago:</strong> {eventItem.payment_type || '-'}</Col>
            <Col md={6}><strong>Cupo:</strong> {eventItem.capacity_limit || '-'}</Col>
            <Col md={6}><strong>Conteo:</strong> {eventItem.registration_count_mode || '-'}</Col>
            <Col md={12}><strong>Descripcion:</strong> {eventItem.description || 'Sin descripcion'}</Col>
          </Row>

          {(eventItem.event_sessions || []).length ? (
            <div className="d-flex flex-column gap-3">
              {(eventItem.event_sessions || []).map((session) => (
                <Card key={session.event_session_id || `${session.session_date}-${session.start_time}`}>
                  <Card.Body>
                    <Row className="g-2 small">
                      <Col md={6}><strong>Fecha:</strong> {session.session_date || '-'}</Col>
                      <Col md={3}><strong>Hora inicio:</strong> {formatSessionTime(session.start_time)}</Col>
                      <Col md={3}><strong>Hora fin:</strong> {formatSessionTime(session.end_time)}</Col>
                      <Col md={6}><strong>Lugar:</strong> {session.location_name || 'Sin lugar definido'}</Col>
                      <Col md={6}><strong>Direccion:</strong> {session.location_address || 'Sin direccion definida'}</Col>
                      <Col md={12}>
                        <strong>Enlace:</strong>{' '}
                        {session.meeting_url ? (
                          <a href={session.meeting_url} target="_blank" rel="noreferrer">{session.meeting_url}</a>
                        ) : (
                          'Sin enlace'
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <Alert variant="info" className="mb-0">No hay sesiones registradas para este evento.</Alert>
          )}

          {registration ? (
            <Alert variant="success" className="mb-0">Ya estas inscrito en este evento.</Alert>
          ) : (
            <div className="d-flex flex-column gap-2">
              {eventItem.registration_count_mode === 'people' && (
                <Form.Group>
                  <Form.Label>Numero de personas</Form.Label>
                  <Form.Control type="number" min={1} value={peopleCount || 1} onChange={(e) => onPeopleChange(e.target.value)} />
                </Form.Group>
              )}
              <Button disabled={busy} onClick={() => onRegister(eventItem)}>
                {busy ? 'Registrando...' : 'Inscribirme'}
              </Button>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

function EventsMember() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState('');
  const [error, setError] = useState('');
  const [okMessage, setOkMessage] = useState('');
  const [peopleByEvent, setPeopleByEvent] = useState({});
  const [cycleYear, setCycleYear] = useState(new Date().getFullYear());
  const [checkoutRegistrationId, setCheckoutRegistrationId] = useState(null);
  const [sessionDetails, setSessionDetails] = useState({ show: false, title: '', sessions: [] });
  const [searchEventId, setSearchEventId] = useState('');
  const [searchingEvent, setSearchingEvent] = useState(false);
  const [lookupEvent, setLookupEvent] = useState(null);
  const [showLookupModal, setShowLookupModal] = useState(false);

  const stripePromise = loadStripe(stripeKey);

  const registrationByEvent = useMemo(() => {
    const map = {};
    registrations.forEach((row) => {
      map[row.event_id || row.event?.event_id] = row;
    });
    return map;
  }, [registrations]);

  useEffect(() => {
    document.title = 'FAMA - Eventos';
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [availableEvents, myRows] = await Promise.all([
        listAvailableEvents(token, { cycleYear, statusFilter: 'upcoming' }),
        listMyRegistrations(token, { cycleYear }),
      ]);
      setEvents(Array.isArray(availableEvents) ? availableEvents : []);
      setRegistrations(Array.isArray(myRows) ? myRows : []);
    }
    catch (e) {
      setError(e.message || 'No se pudieron cargar eventos.');
    }
    finally {
      setLoading(false);
    }
  }

  async function onRegister(eventItem) {
    const key = `register-${eventItem.event_id}`;
    setActionKey(key);
    setError('');
    setOkMessage('');
    try {
      const requestedPeople = eventItem.registration_count_mode === 'people'
        ? parseInt(peopleByEvent[eventItem.event_id] || 1)
        : 1;

      await registerToEvent(token, eventItem.event_id, {
        peopleCount: requestedPeople,
      });
      setOkMessage('Registro completado.');
      setShowLookupModal(false);
      await loadData();
    }
    catch (e) {
      setError(e.message || 'No se pudo registrar al evento.');
    }
    finally {
      setActionKey('');
    }
  }

  function onPayWithStripe(registration) {
    setCheckoutRegistrationId(registration.event_registration_id);
    setError('');
    setOkMessage('');
  }

  function openSessionDetails(title, sessions) {
    setSessionDetails({ show: true, title, sessions });
  }

  async function searchEventById() {
    if (!searchEventId) {
      setError('Captura un ID de evento.');
      return;
    }

    setSearchingEvent(true);
    setError('');
    setOkMessage('');

    try {
      const eventItem = await getAvailableEventById(token, searchEventId);
      setLookupEvent(eventItem);
      setShowLookupModal(true);
    }
    catch (e) {
      setError(e.message || 'No se pudo consultar el evento.');
    }
    finally {
      setSearchingEvent(false);
    }
  }

  async function onCheckoutComplete() {
    const registrationId = checkoutRegistrationId;
    setOkMessage('Pago recibido. Verificando estado con Stripe...');
    setError('');

    try {
      if (registrationId) {
        await finalizeEventRegistrationPayment(token, registrationId);
      }
      setOkMessage('Pago confirmado.');
    }
    catch (e) {
      setError(e.message || 'No se pudo confirmar el pago con Stripe.');
    }
    finally {
      setCheckoutRegistrationId(null);
      await loadData();
    }
  }

  async function onDownloadCertificate(registration) {
    const key = `cert-${registration.event_registration_id}`;
    setActionKey(key);
    setError('');
    setOkMessage('');

    try {
      await downloadCertificate(token, registration.event_registration_id);
      setOkMessage('Certificado descargado.');
      await loadData();
    }
    catch (e) {
      setError(e.message || 'No se pudo descargar certificado.');
    }
    finally {
      setActionKey('');
    }
  }

  return (
    <Container className="py-4">
      <Row className="mb-3 align-items-end">
        <Col>
          <h1 className="mb-1">Eventos</h1>
          <p className="text-muted mb-0">Inscribete a eventos disponibles y consulta tus registros.</p>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Ciclo</Form.Label>
            <Form.Control type="number" value={cycleYear} onChange={e => setCycleYear(e.target.value)} />
          </Form.Group>
        </Col>
        <Col md="auto">
          <Button onClick={loadData}>Actualizar</Button>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header><strong>Buscar evento por ID</strong></Card.Header>
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Group controlId="member-event-search-id">
                <Form.Label>ID del evento</Form.Label>
                <Form.Control value={searchEventId} onChange={e => setSearchEventId(e.target.value)} placeholder="Ej. 123" />
              </Form.Group>
            </Col>
            <Col md="auto">
              <Button onClick={searchEventById} disabled={searchingEvent}>{searchingEvent ? 'Buscando...' : 'Buscar'}</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!!error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {!!okMessage && <Alert variant="success" dismissible onClose={() => setOkMessage('')}>{okMessage}</Alert>}

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Eventos disponibles</strong>
          <Badge bg="secondary">{events.length}</Badge>
        </Card.Header>
        <Card.Body style={{ overflowX: 'auto' }}>
          {loading ? (
            <div className="text-center py-4"><Spinner /></div>
          ) : (
            <Table striped hover>
              <thead>
                <tr>
                  <th>Evento</th>
                  <th>Tipo</th>
                  <th>Inicio</th>
                  <th>Modalidad</th>
                  <th>Pago</th>
                  <th>Cupo</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {events.map((eventItem) => {
                  const myRegistration = registrationByEvent[eventItem.event_id];
                  return (
                    <tr key={`event-open-${eventItem.event_id}`}>
                      <td>{eventItem.title}</td>
                      <td>{eventItem.event_type}</td>
                      <td>{eventItem.starts_on}</td>
                      <td>{eventItem.modality}</td>
                      <td>{eventItem.payment_type}</td>
                      <td>{eventItem.capacity_limit || '-'}</td>
                      <td>
                        {!myRegistration ? (
                          <div className="d-flex flex-column gap-2">
                            {eventItem.registration_count_mode === 'people' && (
                              <Form.Control
                                size="sm"
                                type="number"
                                min={1}
                                value={peopleByEvent[eventItem.event_id] || 1}
                                onChange={e => setPeopleByEvent(prev => ({ ...prev, [eventItem.event_id]: e.target.value }))}
                              />
                            )}
                            <Button
                              size="sm"
                              disabled={actionKey === `register-${eventItem.event_id}`}
                              onClick={() => onRegister(eventItem)}
                            >
                              Inscribirme
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => openSessionDetails(eventItem.title, eventItem.event_sessions)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        ) : (
                          <div className="d-flex flex-column gap-2 align-items-start">
                            <span className="text-success">Ya inscrito</span>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => openSessionDetails(eventItem.title, eventItem.event_sessions)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!events.length && (
                  <tr><td colSpan={7} className="text-center text-muted">No hay eventos disponibles.</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Mis inscripciones</strong>
          <Badge bg="secondary">{registrations.length}</Badge>
        </Card.Header>
        <Card.Body style={{ overflowX: 'auto' }}>
          {loading ? (
            <div className="text-center py-4"><Spinner /></div>
          ) : (
            <Table striped hover>
              <thead>
                <tr>
                  <th>Evento</th>
                  <th>Estado</th>
                  <th>Pago</th>
                  <th>Asistencia</th>
                  <th>Certificado</th>
                  <th>Sesiones</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((registration) => {
                  const attended = (registration.event_attendances || []).filter(a => a.attended).length;
                  const totalSessions = registration.event?.event_sessions?.length || 0;
                  return (
                    <tr key={`my-reg-${registration.event_registration_id}`}>
                      <td>{registration.event?.title}</td>
                      <td>{registration.registration_status}</td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <span>{registration.payment_status}</span>
                          {registration.event?.payment_type === 'stripe' && registration.payment_status !== 'pagado' && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={actionKey === `pay-${registration.event_registration_id}`}
                              onClick={() => onPayWithStripe(registration)}
                            >
                              Pagar
                            </Button>
                          )}
                        </div>
                      </td>
                      <td>{attended}/{totalSessions}</td>
                      <td>
                          {(registration.certificate_status === 'listo' || registration.certificate_status === 'emitido') ? (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={actionKey === `cert-${registration.event_registration_id}`}
                              onClick={() => onDownloadCertificate(registration)}
                            >
                              PDF
                            </Button>
                          ) : (
                            <span className="text-muted">Pendiente</span>
                          )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openSessionDetails(registration.event?.title || 'evento', registration.event?.event_sessions)}
                        >
                          Ver detalles
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!registrations.length && (
                  <tr><td colSpan={6} className="text-center text-muted">Aun no tienes inscripciones.</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!checkoutRegistrationId} onHide={() => setCheckoutRegistrationId(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Pago de evento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!!checkoutRegistrationId && (
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{
                fetchClientSecret: fetchEventCheckoutClientSecret(checkoutRegistrationId, token),
                onComplete: onCheckoutComplete,
              }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </Modal.Body>
      </Modal>

      <SessionDetailsModal
        show={sessionDetails.show}
        onHide={() => setSessionDetails({ show: false, title: '', sessions: [] })}
        title={sessionDetails.title}
        sessions={sessionDetails.sessions}
      />

      <EventLookupModal
        show={showLookupModal}
        onHide={() => setShowLookupModal(false)}
        eventItem={lookupEvent}
        registration={lookupEvent ? registrationByEvent[lookupEvent.event_id] : null}
        peopleCount={lookupEvent ? (peopleByEvent[lookupEvent.event_id] || 1) : 1}
        onPeopleChange={(value) => {
          if (!lookupEvent) {
            return;
          }
          setPeopleByEvent(prev => ({ ...prev, [lookupEvent.event_id]: value }));
        }}
        onRegister={onRegister}
        busy={lookupEvent ? actionKey === `register-${lookupEvent.event_id}` : false}
      />
    </Container>
  );
}

export default EventsMember;
